import { memo } from 'react';
import type { RefObject } from 'react';

import { Th, Thead, Tr } from '@frontend/legacy-components';

import type {
  ColumnConfig,
  RowDataConstraint,
  SortParam,
  SortValue,
} from '../types';
import { MemoizedHeaderCellContent } from './HeaderCellContent';

export type TableHeaderProps<
  ColumnId extends string,
  RowData extends RowDataConstraint,
> = {
  columns: ColumnConfig<ColumnId, RowData>[];
  currentSort: SortValue;
  onSort: (sortKey: SortParam | null) => void;
  stickyLeftColumnId: ColumnId;
  stickyTopLeftElementRef?: RefObject<HTMLTableCellElement> | null;
};

export const TableHeader = <
  ColumnId extends string,
  RowData extends RowDataConstraint,
>({
  columns,
  currentSort,
  onSort,
  stickyLeftColumnId,
  stickyTopLeftElementRef,
}: TableHeaderProps<ColumnId, RowData>) => {
  return (
    <Thead position="sticky" top={0} bg="page.plateLight" zIndex={2}>
      <Tr>
        {columns.map((column) => {
          const {
            columnId,
            title: columnTitle,
            sortKey: columnSortKey,
            width: columnWidth,
            horizontalAlignment: columnHorizontalAlignment,
          } = column;

          const isStickyLeftColumn = columnId === stickyLeftColumnId;

          return (
            <Th
              key={columnId}
              position="relative"
              width={columnWidth}
              padding={0}
              borderWidth={0}
              sx={{
                ':not(:last-of-type)': {
                  borderWidth: 0,
                },
              }}
              whiteSpace="nowrap"
              {...(isStickyLeftColumn && {
                ref: stickyTopLeftElementRef,
                position: 'sticky',
                zIndex: 2,
                left: 0,
                borderRightWidth: 1,
                sx: {
                  ':not(:last-of-type)': {
                    borderRightWidth: 1,
                  },
                },
              })}
            >
              <MemoizedHeaderCellContent
                {...{
                  columnTitle,
                  columnSortKey,
                  columnHorizontalAlignment,
                  currentSort,
                  onSort,
                }}
              />
            </Th>
          );
        })}
      </Tr>
    </Thead>
  );
};

export const MemoizedTableHeader = memo(TableHeader);
